import clsx from 'clsx';

import styles from './seatMap.module.css';

export interface SeatMapItem {
  id?: string;
  showName?: string;
  available?: boolean;
  seatTypeCode?: string;
}

export interface SeatMapProps<T extends SeatMapItem = SeatMapItem> {
  seats: readonly T[];
  selectedIds: readonly string[];
  seatLabel: (seat: T) => string;
  onToggle: (seat: T) => void;
}

const SELECTABLE_SEAT_TYPES = ['Normal', 'Foreground', 'Background'];
const VISIBLE_SEAT_TYPES = [...SELECTABLE_SEAT_TYPES, 'Adjustment'];

interface SeatPosition {
  row: number;
  column: string;
}

const parseSeatName = (showName?: string): SeatPosition | null => {
  const match = showName?.match(/^(\d+)([A-Za-z]+)$/);
  if (!match) return null;

  const [, row, column] = match;
  if (!row || !column) return null;

  return {
    row: Number(row),
    column: column.toUpperCase()
  };
};

const SeatMap = <T extends SeatMapItem>({
  seats,
  selectedIds,
  seatLabel,
  onToggle
}: SeatMapProps<T>) => {
  const placedSeats = seats
    .filter(seat => VISIBLE_SEAT_TYPES.includes(seat.seatTypeCode ?? ''))
    .map(seat => ({
      seat,
      position: parseSeatName(seat.showName ?? seat.id)
    }))
    .filter(
      (
        entry
      ): entry is { seat: T; position: NonNullable<typeof entry.position> } =>
        entry.position !== null
    );

  const columnsByRow = new Map<number, string[]>();
  placedSeats.forEach(({ position }) => {
    const columns = columnsByRow.get(position.row) ?? [];
    columns.push(position.column);
    columnsByRow.set(position.row, columns);
  });

  const columns: string[] = [];
  columnsByRow.forEach(rowColumns => {
    if (rowColumns.length > columns.length) {
      columns.splice(0, columns.length, ...rowColumns);
    }
  });
  placedSeats.forEach(({ position }) => {
    if (!columns.includes(position.column)) columns.push(position.column);
  });

  const columnIndexes = new Map(
    columns.map((column, index) => [column, index])
  );
  const rowNumbers = Array.from(
    new Set(placedSeats.map(({ position }) => position.row))
  );
  const rows = rowNumbers.map(rowNumber => {
    const row: Array<T | null> = columns.map(() => null);
    placedSeats
      .filter(({ position }) => position.row === rowNumber)
      .forEach(({ seat, position }) => {
        const columnIndex = columnIndexes.get(position.column);
        if (columnIndex !== undefined) row[columnIndex] = seat;
      });
    return row;
  });

  return (
    <div className={styles.map}>
      {rows.map((row, rowIndex) => (
        <div key={rowNumbers[rowIndex]} className={styles.row}>
          {row.map((seat, columnIndex) => {
            if (!seat) {
              return (
                <span
                  key={`${rowNumbers[rowIndex]}-${columns[columnIndex]}`}
                  className={styles.empty}
                />
              );
            }

            const selected = Boolean(seat.id && selectedIds.includes(seat.id));
            const selectable = Boolean(
              seat.id &&
                seat.available &&
                SELECTABLE_SEAT_TYPES.includes(seat.seatTypeCode ?? '')
            );
            return (
              <button
                key={seat.id ?? `${rowNumbers[rowIndex]}-${columnIndex}`}
                type="button"
                className={clsx(styles.seat, selected && styles.selected)}
                aria-label={seatLabel(seat)}
                aria-pressed={selected}
                disabled={!selectable}
                onClick={() => onToggle(seat)}
              >
                <span>{seat.showName ?? seat.id}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default SeatMap;
